/**
 * Shows case volume broken down by the configurable classification.
 */

import { useCallback, useEffect, useState } from "react";

import {
  InternalApiError,
  type InternalCaseApiClient,
} from "@libro-reclamos/api-client/InternalCases";
import type {
  CaseReasonAnalyticsResponse,
  CaseReasonLevel,
} from "@libro-reclamos/contracts/CaseReasons";

interface CaseReasonAnalyticsPanelProps {
  readonly client: InternalCaseApiClient;
  readonly revision: number;
}

const EmptyAnalytics: CaseReasonAnalyticsResponse = Object.freeze({
  buckets: [],
  groupBy: "REASON",
  totalCases: 0,
  unclassifiedCases: 0,
});

const LevelLabels = Object.freeze({
  CATEGORY: "Categoría",
  REASON: "Motivo",
  SUB_REASON: "Submotivo",
}) satisfies Readonly<Record<CaseReasonLevel, string>>;

const Levels: readonly CaseReasonLevel[] = Object.freeze(["CATEGORY", "REASON", "SUB_REASON"]);

function ErrorMessage(error: unknown): string {
  if (error instanceof InternalApiError && error.status === 403) {
    return "Tu cuenta no tiene acceso a la analítica de esta compañía.";
  }
  return "No pudimos cargar la analítica por motivo.";
}

function Percent(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

/**
 * Ranks the busiest branches of the catalog. The unclassified figure is shown
 * next to the total rather than as a bucket, so a company that has just loaded
 * its catalog can see how much of its history is still uncategorized.
 */
export function CaseReasonAnalyticsPanel({ client, revision }: CaseReasonAnalyticsPanelProps) {
  const [analytics, setAnalytics] = useState<CaseReasonAnalyticsResponse>(EmptyAnalytics);
  const [groupBy, setGroupBy] = useState<CaseReasonLevel>("REASON");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const Load = useCallback(
    async (level: CaseReasonLevel, signal: AbortSignal): Promise<void> => {
      setLoading(true);
      try {
        setAnalytics(await client.caseReasonAnalytics({ groupBy: level }, signal));
        setMessage(null);
      } catch (error: unknown) {
        if (signal.aborted) {
          return;
        }
        setMessage(ErrorMessage(error));
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [client],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void Load(groupBy, controller.signal);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [Load, groupBy, revision]);

  const topShare = analytics.buckets[0]?.caseCount ?? 0;

  return (
    <section
      aria-labelledby="reason-analytics-title"
      className="rounded-[var(--radius-medium)] border border-[var(--color-border-default)] bg-white p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            className="m-0 text-lg font-bold text-[var(--color-brand-deep)]"
            id="reason-analytics-title"
          >
            Casos por motivo
          </h2>
          <p className="mt-1 mb-0 text-sm text-[var(--color-text-muted)]">
            {analytics.totalCases} caso{analytics.totalCases === 1 ? "" : "s"} en tu alcance ·{" "}
            {analytics.unclassifiedCases} sin clasificar
          </p>
        </div>
        <label className="grid gap-1 text-xs font-semibold">
          Agrupar por
          <select
            className="rounded-[var(--radius-small)] border border-[var(--color-border-default)] px-3 py-2 text-sm font-normal"
            disabled={loading}
            onChange={(event) => {
              setGroupBy(event.target.value as CaseReasonLevel);
            }}
            value={groupBy}
          >
            {Levels.map((level) => (
              <option key={level} value={level}>
                {LevelLabels[level]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {message === null ? null : (
        <p className="mt-3 mb-0 text-sm text-[var(--color-status-critical)]" role="status">
          {message}
        </p>
      )}

      {analytics.buckets.length === 0 && !loading && message === null ? (
        <p className="mt-3 mb-0 text-sm text-[var(--color-text-muted)]">
          Todavía no hay casos clasificados en este nivel.
        </p>
      ) : null}

      <ol className="mt-4 mb-0 grid list-none gap-3 pl-0">
        {analytics.buckets.slice(0, 10).map((bucket) => (
          <li key={bucket.id ?? bucket.name}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-semibold text-[var(--color-text-default)]">
                {bucket.path.length === 0 ? "" : `${bucket.path.join(" › ")} › `}
                {bucket.name}
              </span>
              <span className="text-sm text-[var(--color-text-muted)]">
                {bucket.caseCount} · {Percent(bucket.share)}
              </span>
            </div>
            <div
              aria-hidden="true"
              className="mt-1 h-2 rounded-full bg-[var(--color-surface-canvas)]"
            >
              <div
                className="h-2 rounded-full bg-[var(--color-brand-primary)]"
                style={{
                  width:
                    topShare === 0 ? "0%" : `${((bucket.caseCount / topShare) * 100).toFixed(1)}%`,
                }}
              />
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
